import React from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const trustPoints = [
  "Free Consultation",
  "No Obligation",
  "Reply within 1hrs",
  "4+ Years Experience",
];

export default function CTA() {
  const ref = useScrollAnimation();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:wght@700;900&display=swap');
        .cta-heading { font-family: 'Fraunces', serif; }
        .cta-body    { font-family: 'Plus Jakarta Sans', sans-serif; }
        @keyframes ctaPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(1.3); }
        }
        .cta-pulse { animation: ctaPulse 2s infinite; }
      `}</style>

      <section
        id="contact"
        className="cta-body py-20 relative overflow-hidden bg-gradient-to-b from-blue-50 to-sky-100"
      >
        {/* Blobs */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 right-0 w-60 h-60 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-100/40 rounded-full blur-3xl pointer-events-none" />

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent" />

        <div className="max-w-4xl mx-auto px-5 md:px-10 relative">
          <div ref={ref} className="section-animate text-center">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white border border-green-200 rounded-full px-4 py-1.5 mb-8 shadow-sm">
              <span className="text-[10px] font-bold text-green-700 tracking-widest uppercase">
                Ready to Start?
              </span>
            </div>

            {/* Heading */}
            <h2 className="cta-heading font-black text-5xl md:text-7xl text-black leading-tight mb-5">
              Let's Build Something{" "}
              <span
                style={{
                  background: "linear-gradient(135deg,#1d4ed8 0%,#0ea5e9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Great
              </span>
              <br />
              Together.
            </h2>

            {/* Subtext */}
            <p className="cta-body text-slate-500 text-lg font-medium mb-12 max-w-xl mx-auto leading-relaxed">
              From idea to launch — we handle everything. Get a free strategy call and let's figure out how to grow your business.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-14">
              <a
                href="https://wa.me/919253174295"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2.5 bg-green-500 hover:bg-green-600 text-white font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-green-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.555 4.126 1.526 5.859L0 24l6.335-1.507A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.5-5.207-1.375l-.375-.219-3.758.894.952-3.664-.244-.389A9.93 9.93 0 012 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z" />
                </svg>
                WhatsApp Us
              </a>

              <a
                href="tel:+919253174295"
                className="inline-flex items-center gap-2.5 bg-white border border-blue-200 text-blue-600 font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:bg-blue-50 hover:scale-105 shadow-md shadow-blue-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Schedule Call
              </a>

              <a
                href="mailto:info@noshinfotech.com"
                className="inline-flex items-center gap-2.5 bg-white border border-slate-200 text-slate-500 font-semibold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:border-blue-300 hover:text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Requirement
              </a>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap items-center justify-center gap-6">
              {trustPoints.map((t) => (
                <span key={t} className="flex items-center gap-2 text-sm text-slate-400 font-medium">
                  <span className="text-blue-500 text-xs font-bold">✓</span>
                  {t}
                </span>
              ))}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}